import {
  WSActions,
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayoutPreferences } from "@wingspanhq/payments/dist/interfaces";
import {
  AccountExpand,
  PayoutDestinationType,
  PayoutSettingsUpdateStandardDestination,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { useWSMutation } from "@ws-react-query";
import { useEffect, useMemo, useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { WSQueries } from "../../../../query/WSQuery";
import {
  queriesAccounts,
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesPaymentCards,
  queriesPayouts
} from "@query";
import { payoutsApi } from "@api";
import { useSession } from "@hooks/useSession";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorExternalBankAccountIPReady } from "@selectors/selectorExternalBankAccountIPReady";
import { selectorOwnPayoutSettings } from "@selectors/selectorOwnPayoutSettings";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { isCountryUS } from "../../../../shared/selectors/selectorIsDomesticMember";
import { selectorPaymentCardPayoutReady } from "../../../../shared/selectors/selectorPaymentCardPayoutReady";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { selectorIsPlatformBalanceAccount } from "../../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { List } from "../ManageMethods/List";
import { MethodValue } from "../ManageMethods/types";
import { useModalConfirmPayoutMethodChange } from "../ModalConfirmPayoutMethodChange";
import { useDefaultPayoutMethod } from "./DefaultPayoutMethod";
import { useModalInfo } from "./ModalInfo";
import { useInstantPayoutFee } from "../../hooks/useInstantPayoutFee";
import { selectorExternalBankAccountPayoutReady } from "@selectors/selectorExternalBankAccountPayoutReady";

type Props = {
  onSaved: () => void;
  onAddNew: () => void;
  error?: WSServiceError;
};

export const ManagePayoutMethod: React.FC<Props> = props => {
  const { accountId } = useSession();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryPaymentCards = queriesPaymentCards.listPaymentCards.useQuery();
  const queryAccount = queriesAccounts.getAccount.useQuery(
    { accountId, expand: [AccountExpand.COMPLIANCE_ENTITY] },
    { enabled: !!accountId }
  );
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryFeatureFlags = useFeatureFlags();

  const [value, setValue] = useState<MethodValue | null | undefined>();

  const defaultPayoutMethod = useDefaultPayoutMethod();
  useEffect(() => {
    if (defaultPayoutMethod) {
      setValue(defaultPayoutMethod);
    }
  }, [defaultPayoutMethod]);

  const country = queryAccount.data?.complianceEntity?.address?.country;

  const isInternational = queryAccount.data ? !isCountryUS(country) : false;

  // A platform-balance destination is instant-capable regardless of country.
  const isInternationalInstantPayoutReady =
    (queryPayoutSettings.data
      ? selectorPayoutSettingsDefaultDestination(queryPayoutSettings.data)?.type
      : undefined) === PayoutDestinationType.INTERNAL_ACCOUNT ||
    country === "CA";

  const showInstantPayoutMethods = isInternational
    ? queryFeatureFlags.data?.internationalIP &&
      isInternationalInstantPayoutReady
    : true;
  const ipFee = useInstantPayoutFee();
  const modalInfo = useModalInfo();

  const [mutate, meta] = useWSMutation<
    void,
    WSServiceError,
    MethodValue | null
  >(
    async data => {
      if (!data) {
        return;
      }

      if (!data.category) {
        throw new Error("No payout preference specified");
      }

      if (
        !(
          data.category === PayoutPreferences.Standard ||
          data.category === PayoutPreferences.Instant
        )
      ) {
        throw new Error("Invalid payout preference");
      }

      const apPayoutInternalAccount = queryInternalAccounts.data?.data?.find(
        internalAccount =>
          selectorIsPlatformBalanceAccount(
            internalAccount,
            queryPayoutSettings.data
          )
      );

      const payoutSettingsList =
        await queriesPayouts.listPayoutSettings.fetchQueryData();
      const payoutSettingsId =
        selectorOwnPayoutSettings(payoutSettingsList)?.id;

      if (!payoutSettingsId) {
        throw new Error("No payout settings found");
      }

      const resolveDestination =
        (): PayoutSettingsUpdateStandardDestination | null => {
          if (data.type === "InternalAccount") {
            return apPayoutInternalAccount
              ? {
                  type: PayoutDestinationType.INTERNAL_ACCOUNT,
                  id: apPayoutInternalAccount.id
                }
              : null;
          }

          switch (data.type) {
            case "Account":
              return {
                type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT,
                id: data.id
              };
            case "PaymentCard":
              return {
                type: PayoutDestinationType.PAYMENT_CARD,
                id: data.id
              };
            default:
              throw new Error(
                `${data.type} is not a valid payout destination type`
              );
          }
        };

      const destination = resolveDestination();

      if (data.category === PayoutPreferences.Instant) {
        await payoutsApi.updatePayoutSettings({
          payoutSettingsId,
          payoutSettingsUpdate: {
            preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
            instantDestination: destination
          }
        });
        return;
      }

      await payoutsApi.updatePayoutSettings({
        payoutSettingsId,
        payoutSettingsUpdate: {
          preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
          standardDestination: destination
        }
      });
    },
    {
      onSuccess: props.onSaved,
      dependencies: [
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase
      ]
    }
  );

  const modalConfirmPayoutMethodChange = useModalConfirmPayoutMethodChange();

  const isSameMethodSelected = useMemo(
    () =>
      value?.id === defaultPayoutMethod?.id &&
      value?.category === defaultPayoutMethod?.category,
    [value, defaultPayoutMethod]
  );

  return (
    <WSQueries
      queries={{
        queryExternalBankAccounts,
        queryPaymentCards,
        queryInternalAccounts,
        queryPayoutSettings
      }}
    >
      {({
        queryExternalBankAccounts: { data: externalBankAccountList },
        queryPaymentCards: { data: paymentCardList },
        queryInternalAccounts: { data: internalAccounts },
        queryPayoutSettings: { data: payoutSettings }
      }) => {
        return (
          <WSList gap="XL">
            {/* Surface the upstream error (e.g. failed save the user came back from) */}
            <WSErrorMessage contextKey="PayoutSettings" error={props.error} />

            <WSElement>
              <WSText weight="medium" mb="M">
                Standard payout methods
              </WSText>

              <List
                data={{
                  accounts: (externalBankAccountList.data || []).filter(
                    selectorExternalBankAccountPayoutReady
                  ),
                  internalAccounts: (internalAccounts.data || []).filter(
                    internalAccount =>
                      selectorActiveBankingAccount(internalAccount) ||
                      selectorIsPlatformBalanceAccount(
                        internalAccount,
                        payoutSettings
                      )
                  )
                }}
                onChange={setValue}
                value={value}
                category={PayoutPreferences.Standard}
                initialValue={defaultPayoutMethod}
              />

              {showInstantPayoutMethods && (
                <>
                  <WSDivider mt="2XL" mb="3XL" />

                  <WSFlexBox gap="S" alignItems="center" mb="M">
                    <WSText weight="medium">
                      Instant payout methods ({ipFee}% fee)
                    </WSText>

                    <WSIcon
                      name="info-circle-fill"
                      color="gray500"
                      size="S"
                      onClick={() => {
                        modalInfo.open();
                      }}
                    />
                  </WSFlexBox>

                  <List
                    data={{
                      accounts: (externalBankAccountList.data || []).filter(
                        externalBankAccount =>
                          selectorExternalBankAccountIPReady(
                            externalBankAccount,
                            isInternational
                          )
                      ),
                      paymentCards: (paymentCardList.data || []).filter(
                        selectorPaymentCardPayoutReady
                      ),
                      internalAccounts: (internalAccounts.data || []).filter(
                        internalAccount =>
                          selectorActiveBankingAccount(internalAccount) ||
                          selectorIsPlatformBalanceAccount(
                            internalAccount,
                            payoutSettings
                          )
                      )
                    }}
                    onChange={setValue}
                    value={value}
                    category={PayoutPreferences.Instant}
                    initialValue={defaultPayoutMethod}
                  />
                </>
              )}
            </WSElement>

            <WSCard
              onClick={props.onAddNew}
              actions={{
                alignment: "left",
                buttons: [
                  {
                    icon: "plus-circle",
                    kind: "Link",
                    label: "Add new payout method"
                  }
                ]
              }}
            />

            <WSErrorMessage contextKey="PayoutSettings" error={meta.error} />

            <WSActions
              alignment="fill"
              buttons={[
                {
                  label: "Save changes",
                  onClick: () => {
                    if (!defaultPayoutMethod) {
                      mutate(value);
                      return;
                    }
                    modalConfirmPayoutMethodChange.open({
                      onSave: () => {
                        mutate(value);
                      },
                      previousMethod: defaultPayoutMethod,
                      newMethod: value!
                    });
                  },
                  loading: meta.isLoading,
                  disabled: isSameMethodSelected
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSQueries>
  );
};
